'use client';

import React, {useState} from 'react';
import {Link} from '@/i18n/navigation';
import {Box, Typography} from '@mui/material';
import {useLocale} from 'next-intl';
import {trackCtaClick} from '@/lib/analytics';
import {
  StyledCtaCardRoot,
  StyledCtaCardArrowContainer,
  StyledCtaCardContainer
} from '@/components/sections/cta-cards-section/CtaCard.styles';
import RightArrowIcon from '@/components/ui/icons/RightArrow';
import ContentfulImage from '@/components/ui/image/ContentfulImage';
import type {CtaCardVariant} from '@/components/sections/cta-cards-section/CtaCardList';
import {ModalSectionItem} from '@/lib/ensure-link-button';
import PopupSectionModal from '@/components/ui/modal/PopupSectionModal';

type CtaCardProps = {
  heading: string;
  imageUrl: string;
  href: string;
  isExternal?: boolean;
  modalSections?: ReadonlyArray<ModalSectionItem | null> | null;
  breadText?: string | null | undefined;
  focalX?: number | null;
  focalY?: number | null;
  contentId: string;
  variant?: CtaCardVariant;
  skipGradient?: boolean;
};

const CtaCard = React.memo(function CtaCard({
  heading,
  imageUrl,
  href,
  isExternal,
  modalSections,
  breadText,
  focalX,
  focalY,
  contentId,
  variant = 'v1',
  skipGradient = false,
}: CtaCardProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const hasModalSections = Boolean(modalSections && modalSections.some(Boolean));
  const hasBread = Boolean(breadText && breadText.trim() !== '');
  const rootClassName = [hasBread ? 'has-bread' : '', skipGradient ? 'no-gradient' : '']
    .filter(Boolean)
    .join(' ');
  const locale = useLocale();
  const readMoreLabel = locale === 'sv' ? 'Läs mer' : 'Read more';

  const image = (
    <ContentfulImage
      src={imageUrl}
      alt={heading}
      sizes='(max-width: 600px) 100vw, 50vw'
      style={{objectFit: 'cover'}}
      fill
      focalX={focalX}
      focalY={focalY}
    />
  );

  const inner = variant === 'v2' ? (
    <StyledCtaCardRoot data-variant='v2' className={rootClassName}>
      <Box className='media'>{image}</Box>
      <Box className='panel'>
        <Typography className='heading' variant='h4' component='p'>
          {heading}
        </Typography>
        {breadText ? (
          <Typography className='bread' variant='body2'>
            {breadText}
          </Typography>
        ) : null}
        <Box className='read-more' aria-hidden='true'>
          <Typography variant='body2' component='span' sx={{fontWeight: 600}}>
            {readMoreLabel}
          </Typography>
        </Box>
      </Box>
    </StyledCtaCardRoot>
  ) : (
    <StyledCtaCardRoot data-variant='v1' className={rootClassName}>
      {image}
      <Box className='overlay' />

      <StyledCtaCardContainer className='content'>
        <Typography className='heading' variant='h4' component='p' sx={{maxWidth: '85%'}}>
          {heading}
        </Typography>
        {breadText ? (
          <Typography className='bread' variant='body2' sx={{maxWidth: '85%'}}>
            {breadText}
          </Typography>
        ) : null}
      </StyledCtaCardContainer>

      {/* Decorative right arrow in bottom-right corner (non-interactive) */}
      <StyledCtaCardArrowContainer>
        <RightArrowIcon size={24} color='white' />
      </StyledCtaCardArrowContainer>
    </StyledCtaCardRoot>
  );

  // Modal sections take precedence over any link configuration on the card.
  if (hasModalSections) {
    const handleModalClick = () => {
      trackCtaClick({buttonText: heading, targetUrl: href, buttonType: 'modal', contentId});
      setModalOpen(true);
    };

    return (
      <>
        <button
          type='button'
          onClick={handleModalClick}
          aria-label={heading}
          style={{
            display: 'block',
            width: '100%',
            padding: 0,
            border: 'none',
            background: 'none',
            textAlign: 'inherit',
            cursor: 'pointer',
          }}
        >
          {inner}
        </button>
        <PopupSectionModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          sections={modalSections!}
          ariaLabel={heading}
        />
      </>
    );
  }

  const handleClick = () => {
    trackCtaClick({buttonText: heading, targetUrl: href, buttonType: 'cta_card', contentId});
  };

  if (isExternal) {
    return (
      <a
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        style={{textDecoration: 'none'}}
        onClick={handleClick}
        aria-label={heading}
      >
        {inner}
      </a>
    );
  }

  return (
    <Link href={href} onClick={handleClick} aria-label={heading}>
      {inner}
    </Link>
  );
});

export default CtaCard;
